// Recent calls (Stage 4.8f.4a, mockup "Recent calls"): kept by the server, not by this tab (the old phone forgot them on reload) —
// answered, missed (with how many times it rang here), placed; the callbacks still due on top; one tap calls back.
import { useMemo, useState } from "react";
import { Box, Typography, ButtonBase, CircularProgress } from "@mui/material";
import { PhoneIncoming, PhoneOutgoing, PhoneMissed, Phone, CalendarClock } from "lucide-react";
import { useTranslation } from "react-i18next";
import { RowCall, Segments, SectionTitle } from "./phoneUi";
import { fmtDur } from "./phoneFormat";

const TONE = {
  missed: { bg: "var(--danger-bg)", fg: "var(--danger)", Icon: PhoneMissed },
  in:     { bg: "var(--success-bg)", fg: "var(--success)", Icon: PhoneIncoming },
  out:    { bg: "var(--c-blue-bg)", fg: "var(--c-blue)", Icon: PhoneOutgoing },
};

// The list is loaded by the phone (PhoneDock: it also counts today's missed calls for the tab's badge).
export default function PhoneRecent({ ready, onCall, data }) {
  const { t, i18n } = useTranslation("phone");
  const [filter, setFilter] = useState("all");

  const calls = useMemo(() => data?.calls ?? [], [data]);
  const callbacks = data?.callbacks ?? [];

  const label = (c) => c.colleague?.name ? `${c.colleague.name} (${t("extShort", { ext: c.colleague.ext })})` : c.contact?.name || c.number || t("unknown");
  const meta = (c) => {
    if (c.dir === "missed") return [t("missed"), c.section, c.tries > 1 ? t("tries", { count: c.tries }) : null, !c.contact && !c.colleague ? t("notInCrm") : null].filter(Boolean).join(" · ");
    if (c.dir === "out") return c.outcome && c.outcome !== "answered" ? `${t("outgoing")} · ${t(`outcome.${c.outcome}`, { defaultValue: c.outcome })}` : `${t("outgoing")} · ${fmtDur(c.durationSec)}`;
    return `${c.colleague ? t("colleague") : t("incoming")} · ${fmtDur(c.durationSec)}`;
  };
  const time = (iso) => (iso ? new Date(iso).toLocaleTimeString(i18n.language, { hour: "2-digit", minute: "2-digit" }) : "");
  const dayOf = (iso) => {
    if (!iso) return "";
    const d = new Date(iso), today = new Date(), y = new Date(Date.now() - 86400000);
    if (d.toDateString() === today.toDateString()) return t("today");
    if (d.toDateString() === y.toDateString()) return t("yesterday");
    return d.toLocaleDateString(i18n.language, { weekday: "long", day: "numeric", month: "short" });
  };

  const shown = useMemo(() => (filter === "missed" ? calls.filter((c) => c.dir === "missed") : filter === "callback" ? [] : calls), [calls, filter]);
  const groups = [];
  for (const c of shown) { const d = dayOf(c.at); const last = groups[groups.length - 1]; if (last && last.day === d) last.items.push(c); else groups.push({ day: d, items: [c] }); }

  if (!data) return <Box sx={{ p: 4, display: "grid", placeItems: "center" }}><CircularProgress size={20} sx={{ color: "var(--brand-violet)" }} /></Box>;
  const missedCount = calls.filter((c) => c.dir === "missed").length;

  return (
    <Box sx={{ display: "flex", flexDirection: "column", minHeight: 0, flex: 1 }}>
      <Box sx={{ p: "14px 16px 6px" }}>
        <Segments dark label={t("filter")} value={filter} onChange={setFilter}
          items={[{ value: "all", label: t("all") }, { value: "missed", label: missedCount ? `${t("missed")} · ${missedCount}` : t("missed") },
            { value: "callback", label: callbacks.length ? `${t("callBack")} · ${callbacks.length}` : t("callBack") }]} />
      </Box>

      {(filter === "all" || filter === "callback") && callbacks.slice(0, filter === "all" ? 1 : 10).map((b) => (
        <Box key={b.id} sx={{ m: "6px 16px 4px", p: "10px 12px", borderRadius: "12px", background: "var(--warning-bg)", display: "flex", alignItems: "center", gap: "10px" }}>
          <CalendarClock size={18} strokeWidth={1.8} aria-hidden="true" style={{ color: "var(--warning)", flexShrink: 0 }} />
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography sx={{ font: "600 13px/1.35 var(--font-ui)", color: "var(--ink-800)" }}>
              {t("callBackDue", { name: b.name || b.number, time: time(b.at) })}
            </Typography>
            {b.notes && <Typography noWrap sx={{ font: "500 12px/1.35 var(--font-ui)", color: "var(--ink-600)" }}>{b.notes}</Typography>}
          </Box>
          <ButtonBase onClick={() => onCall(b.number, b.name ?? "")} disabled={!ready}
            sx={{ minHeight: 36, px: "12px", borderRadius: "10px", background: "var(--surface-card)", border: "1px solid var(--line-200)", font: "700 12px/1 var(--font-ui)", color: "var(--ink-900)" }}>
            {t("callNow")}
          </ButtonBase>
        </Box>
      ))}
      {filter === "callback" && !callbacks.length && <Typography sx={{ p: "16px", font: "500 13px/1.4 var(--font-ui)", color: "var(--ink-500)" }}>{t("noCallbacks")}</Typography>}

      <Box sx={{ overflowY: "auto", flex: 1, pb: 1 }}>
        {groups.map((g) => (
          <Box key={g.day}>
            <SectionTitle>{g.day}</SectionTitle>
            <Box component="ul" sx={{ listStyle: "none", m: 0, p: "0 8px" }}>
              {g.items.map((c) => {
                const tone = TONE[c.dir] ?? TONE.in;
                const name = label(c);
                return (
                  <Box component="li" key={`${c.dir}-${c.id}`} sx={{ display: "flex", alignItems: "center", gap: "10px", p: "8px", borderRadius: "12px" }}>
                    <Box aria-hidden="true" sx={{ width: 36, height: 36, flexShrink: 0, borderRadius: "50%", display: "grid", placeItems: "center", background: tone.bg, color: tone.fg }}>
                      <tone.Icon size={17} strokeWidth={1.8} />
                    </Box>
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Typography dir="auto" noWrap sx={{ font: "700 14px/1.2 var(--font-ui)", fontFamily: "var(--font-ui), var(--font-ar)", color: c.dir === "missed" ? "var(--danger)" : "var(--ink-900)" }}>{name}</Typography>
                      <Typography noWrap sx={{ font: "500 12px/1.3 var(--font-ui)", color: "var(--ink-500)" }}>{meta(c)}</Typography>
                    </Box>
                    <Typography sx={{ font: "500 12px/1 var(--font-ui)", color: "var(--ink-500)", fontVariantNumeric: "tabular-nums" }}>{time(c.at)}</Typography>
                    <RowCall icon={Phone} label={t("callName", { name })} disabled={!ready || !c.number || c.contact?.doNotContact}
                      onClick={() => onCall(c.colleague?.ext ?? c.number, c.colleague?.name ?? c.contact?.name ?? "")} />
                  </Box>
                );
              })}
            </Box>
          </Box>
        ))}
        {filter !== "callback" && !shown.length && <Typography sx={{ p: "16px", font: "500 13px/1.4 var(--font-ui)", color: "var(--ink-500)" }}>{t(filter === "missed" ? "noMissed" : "noRecent")}</Typography>}
      </Box>
      <Typography sx={{ p: "10px 16px", borderTop: "1px solid var(--line-100)", font: "500 12px/1.4 var(--font-ui)", color: "var(--ink-500)" }}>{t("recentFoot")}</Typography>
    </Box>
  );
}
